//CostQualityAnalysis.tsx
import React from 'react'
import { Card, Row, Col, Statistic, Progress, Tag, Tooltip, Typography, Badge, Rate } from 'antd'
import { TrophyOutlined, InfoCircleOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

interface AnalysisItem {
  model: string
  cost: number
  score: number
  costPerPoint: number
  valueRating: number
  breakdown: any
  rank: number
  efficiency: number
  qualityPenalty: boolean
}

interface MultiRunResultsMeta {
  totalRuns: number
}

interface CostQualityAnalysisProps {
  enhancedCostAnalysis: AnalysisItem[]
  multiRunResults: MultiRunResultsMeta | null
  getScoreColor: (score: number) => string
}

const CostQualityAnalysis: React.FC<CostQualityAnalysisProps> = ({
  enhancedCostAnalysis,
  multiRunResults,
  getScoreColor,
}) => {
  if (!enhancedCostAnalysis || enhancedCostAnalysis.length === 0) return null

  return (
    <Card
      title={
        <span>
          <Title level={4} style={{ margin: 0 }}>
            Cost vs Quality Analysis
            {multiRunResults && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Averaged from {multiRunResults.totalRuns} runs
              </Tag>
            )}
          </Title>
          <Tooltip title="Models ranked by efficiency score (quality³ / cost for high quality, with penalties for low quality)">
            <InfoCircleOutlined style={{ color: 'var(--text-secondary)', marginLeft: 8 }} />
          </Tooltip>
        </span>
      }
    >
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card size="small" title="Top Value Models" bordered={false}>
            <Row gutter={[16, 16]}>
              {enhancedCostAnalysis.slice(0, 3).map((model, index) => (
                <Col span={8} key={model.model}>
                  <Badge.Ribbon
                    text={`#${model.rank}`}
                    color={index === 0 ? 'gold' : index === 1 ? 'silver' : 'bronze'}
                  >
                    <Card size="small">
                      <Row gutter={8}>
                        <Col span={24}>
                          <Text strong>{model.model}</Text>
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Quality"
                            value={model.score.toFixed(1)}
                            prefix={<TrophyOutlined />}
                            valueStyle={{ color: getScoreColor(model.score), fontSize: 18 }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Cost"
                            value={model.cost.toFixed(4)}
                            prefix="$"
                            valueStyle={{ fontSize: 18 }}
                          />
                        </Col>
                        <Col span={24}>
                          <Progress
                            percent={Math.min(model.efficiency * 10, 100)}
                            showInfo={false}
                            strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Efficiency: {model.efficiency.toFixed(2)}
                          </Text>
                          <div>
                            <Tooltip
                              title={`Translation: $${(model.breakdown?.totalCost ?? 0).toFixed(5)} • Evaluation: $${(model.breakdown?.evalTotalCost ?? 0).toFixed(5)} • Total: $${(((model.breakdown?.totalCost ?? 0) + (model.breakdown?.evalTotalCost ?? 0))).toFixed(5)}`}
                            >
                              <Tag>
                                T ${(model.breakdown?.totalCost ?? 0).toFixed(5)} | E ${(model.breakdown?.evalTotalCost ?? 0).toFixed(5)} | Tot ${(((model.breakdown?.totalCost ?? 0) + (model.breakdown?.evalTotalCost ?? 0))).toFixed(5)}
                              </Tag>
                            </Tooltip>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Badge.Ribbon>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card size="small" bordered={false}>
            <Row>
              <Col span={24}>
                {/* Detailed comparison table */}
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 8 }}>Rank</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Model</th>
                      <th style={{ textAlign: 'center', padding: 8 }}>Quality Score</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Total Cost</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Cost Breakdown</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Cost/Point</th>
                      <th style={{ textAlign: 'center', padding: 8 }}>Efficiency</th>
                      <th style={{ textAlign: 'center', padding: 8 }}>Value Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enhancedCostAnalysis.map((record) => (
                      <tr key={record.model} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 8 }}>
                          <span>
                            <Tag color={record.rank <= 3 ? 'gold' : record.rank <= 5 ? 'blue' : 'default'}>
                              #{record.rank}
                            </Tag>
                            {record.qualityPenalty && (
                              <Tag color="red" style={{ marginLeft: 6 }} title="Quality below threshold (7.0) - severe penalty applied">
                                !
                              </Tag>
                            )}
                          </span>
                        </td>
                        <td style={{ padding: 8 }}>
                          <Text strong>{record.model}</Text>
                        </td>
                        <td style={{ padding: 8, textAlign: 'center' }}>
                          <Tag color={getScoreColor(record.score)}>{record.score.toFixed(2)}</Tag>
                        </td>
                        <td style={{ padding: 8, textAlign: 'right' }}>${record.cost.toFixed(5)}</td>
                        <td style={{ padding: 8, textAlign: 'left' }}>
                          <Tooltip title={`Translation: $${(record.breakdown?.totalCost ?? 0).toFixed(5)} • Evaluation: $${(record.breakdown?.evalTotalCost ?? 0).toFixed(5)} • Total: $${(((record.breakdown?.totalCost ?? 0) + (record.breakdown?.evalTotalCost ?? 0))).toFixed(5)}`}>
                            <Tag>
                              T ${(record.breakdown?.totalCost ?? 0).toFixed(5)} | E ${(record.breakdown?.evalTotalCost ?? 0).toFixed(5)} | Tot ${(((record.breakdown?.totalCost ?? 0) + (record.breakdown?.evalTotalCost ?? 0))).toFixed(5)}
                            </Tag>
                          </Tooltip>
                        </td>
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          {record.costPerPoint === Infinity ? 'N/A' : `$${record.costPerPoint.toFixed(5)}`}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center' }}>
                          <div style={{ display: 'inline-block', minWidth: 120 }}>
                            <Progress
                              percent={Math.min(record.efficiency * 10, 100)}
                              size="small"
                              format={() => record.efficiency.toFixed(1)}
                            />
                          </div>
                        </td>
                        <td style={{ padding: 8, textAlign: 'center' }}>
                          <Rate disabled defaultValue={record.valueRating} style={{ fontSize: 14 }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Card>
  )
}

export default CostQualityAnalysis
